import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BpmnModelerService} from '@ispm-client/modules/bpmn-modeler/service/bpmn-modeler.service';
import {EventType, InjectionNames} from '@ispm/shared/bpmn-io/bpmn-js';
import {Connection, Shape} from 'diagram-js/lib/model';
import {Subject} from 'rxjs/Subject';
import {initalDiagram} from '@ispm-client/modules/bpmn-modeler/modeler.component/initial-diagram';
import {select, Store} from '@ngrx/store';
import {map, take, tap, takeUntil, first} from 'rxjs/operators';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {
  ClearNodeConfigurationData,
  NodeCreated,
  NodeDeleted,
  NodesClipboardReset,
  NodesInit,
  NodeUpdated,
  RootNodeUpdatedName,
  SetBpmnIdentifier,
  SetModelerState
} from '@ispm-client/modules/bpmn-modeler/store/actions';
import {HEAD_NODE_ID, subProcessType, taskType} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {ModelerPanelService} from '@ispm-client/modules/bpmn-modeler/service/modeler-panel.service';
import {PanelPosition} from '@ispm/layout/components/panel/panel.component';
import {
  selectModelerContext,
  selectNodesCreationInfo
} from '@ispm-client/modules/bpmn-modeler/store/selectors';

import {BpmnNodeCreatedResult} from '@ispm-client/modules/bpmn-modeler/models/bpmn-node-created-result';
import {TreatmentPlanSelectorResult} from '@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {Observable, Subscription} from 'rxjs';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {
  AttachmentConfig,
  BpmnModelerConfig,
  ComponentToLoadConfig,
  CreationPanelConfig,
  NodeCreationPanelConfig
} from '@ispm-client/modules/bpmn-modeler/models/bpmn-modeler-config';
import {BpmnXmlService} from '@ispm/shared/bpmn/services/bpmn-xml.service';
import {BpmnModelerData} from '@ispm-client/modules/bpmn-modeler/models/bpmn-modeler-data';
import {NodeCreationResult} from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';
import {PathwaysAuthoringState} from '@ispm/shared/constants/pathways-authoring-state';
import {UpdateNodeId} from '@ispm-client/modules/bpmn-modeler/store/actions/node-id-generator.action';
import {camelCase, cloneDeep, get, has, isEmpty, keys, uniq} from 'lodash';
import {PathwaysViewerCommands} from '@ispm/shared/bpmn-io/pathways-viewer-commands';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {
  FloatedNodesAdded,
  FloatedNodesRemoved
} from "@ispm-client/modules/bpmn-modeler/store/actions/floating-nodes.actions";
import {selectSpinnerStatus} from "@ispm-client/modules/bpmn-modeler/store/selectors/spinner-state.selector";
import {ToggleModelerSpinner} from "@ispm-client/modules/bpmn-modeler/store/actions/spinner-status.actions";
import {ModalTypes} from "@ispm/layout/components/modals/contants/modal-types.enum";
import {ConfirmModalComponent} from "@ispm/layout/components/modals/confirm-modal/confirm-modal.component";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {END_NODE_ID} from '../../../../../../ispm-web/shared/modules/bpmn/index';
import {modalResponse} from "@ispm/layout/constants/modal-responses.enum";
import {
  selectNumberOfFloatedNodesByLayer
} from "@ispm-client/modules/bpmn-modeler/store/selectors/floating-nodes.selector";
import {
  NodePanelConfigurationService
} from "@ispm-client/modules/bpmn-modeler/service/node-panel-configuration.service";
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {SetModelerContext} from "@ispm-client/modules/bpmn-modeler/store/actions/modeler-context.action";

type NodeBuilderMode = 'create' | 'edit' | 'none';

@Component({
  selector: 'ispm-modeler',
  templateUrl: './modeler.component.html',
  styleUrls: ['./modeler.component.scss']
})

export class ModelerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  headNodeName: string;

  private _data: BpmnModelerData;
  @Input()
  set data(data: BpmnModelerData) {
    this._data = data;
    this.initStore(this.getBpmnIdentifier());
  }

  get data(): BpmnModelerData {
    return this._data;
  }

  @Input() hideBpmnLogo: boolean = true;
  @Output() modelerRendered: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() nodeSelected: EventEmitter<any> = new EventEmitter<any>();
  panelName = ModelerPanelService.ModelerPanelName;
  panelPosition = PanelPosition.RIGHT;
  spinnerActive: Observable<boolean>;
  @Output() nodeDataUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Input()
  private config: BpmnModelerConfig;
  @Input()
  private state: PathwaysAuthoringState;
  @Input()
  private revisedEntitiesRevisionsData: any;
  @Input()
  private performTreeRepair: string;
  @Input()
  private discipline: string;
  @Input()
  private knowledgeBase: string;
  private modeler;
  private destroyed$: Subject<any> = new Subject();
  private nodeBuilderMode: NodeBuilderMode = 'none';
  private pathwaysModeling: PathwaysModeling;
  private pathwaysViewerCommands: PathwaysViewerCommands;
  private useDblClick: boolean = true;
  @Input() private panelOpenReadOnly: boolean = false;
  private prefixForProcessID: string = "processNumber";
  private timer;
  private selectedNode;
  protected modalSubscriber$: Subscription;
  @Output() toggleRightPanel: EventEmitter<boolean> = new EventEmitter<boolean>();
  public showSidebar: boolean = true;
  public singleOn: boolean = true;
  public sideBarExpanderTitle: string = "";
  @Input() public useSideBarComponent: boolean = false;
  @Input() public clearNodeDataOnNodeUnSelection: boolean = false;
  @Input() public globalNotificationClass: string;
  @Output() toggleRightSidebar: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private bpmnModelerService: BpmnModelerService,
              private store$: Store<State>,
              private modelerPanelService: ModelerPanelService,
              private nodePanelConfigurationService: NodePanelConfigurationService,
              private bpmnXmlService: BpmnXmlService,
              protected coreAC: CoreActionCreatorService,
              private modalLoader: DynamicModalLoaderService,
              private notificationMessageService: NotificationMessageService) {
    this.spinnerActive = this.store$.pipe(select(selectSpinnerStatus), takeUntil(this.destroyed$));
  }

  ngOnInit() {
    this.getBpmnServices();
    this.useDblClick = get(this.config, 'behavior.useDblClick', true);
    this.pathwaysModeling = this.modeler.get(InjectionNames.pathwaysModeling);
    this.pathwaysViewerCommands = this.modeler.get(InjectionNames.pathwaysViewerCommands);
    const bpmnIdentifier = this.getBpmnIdentifier();
    this.toggleSpinner();
    this.handleLoadingPathway(bpmnIdentifier);
  }

  async handleLoadingPathway(bpmnIdentifier) {
    try {
      if (this.data) {
        this.bpmnXmlService.getBpmnXml(bpmnIdentifier.deploymentId, bpmnIdentifier.name, this.knowledgeBase).subscribe(async (res) => {
          if (res) {
            await this.modeler.importXML(res);
            this.modelerRendered.emit(true);
            // set focus here, calculated by canvas size
            this.modeler.get(InjectionNames.shapeFocus).setInitialFocus(this.config ? !!this.config.verticalLayoutMode : false);
          } else {
            this.notificationMessageService.error(`No bpmn process found for name - ${bpmnIdentifier.name}`);
          }
          this.toggleSpinner();
        })

      } else {
        this.addOnRootNodeAdded();
        const importInitialDiagram = this.generateInitialDiagram(initalDiagram, this.headNodeName, this.discipline);
        await this.modeler.importXML(importInitialDiagram);
        this.toggleSpinner();
        // set focus here, calculated by canvas size
        this.modeler.get(InjectionNames.shapeFocus).setInitialFocus(this.config ? !!this.config.verticalLayoutMode : false);
      }
    } catch (err) {
      this.notificationMessageService.error(`Error loading pathways - ${err.message}`);
      this.toggleSpinner(err.message);
    }
  };

  ngAfterViewInit(): void {
    this.addNewNodeClicked();
    this.addOnNodeDeleted();
    this.addOnNodeUpdated();
    this.addOnFloatedNodesAdded();
    this.addOnFloatedNodesRemoved();
    this.addOnToggleSpinner();
    this.addOnOpenDeletePopup();

    if (this.useDblClick) {
      this.addOnDblClickAndOpenPanel();
      this.addOnClick();
    } else {
      this.stopOnDoubleClick();
      this.addOnSelectionChange();
    }
  }

  getBpmnServices(): void {
    this.modeler = this.bpmnModelerService.getBpmnModeler(
      this.state,
      this.getCurrentCounter(),
      this.performTreeRepair,
      this.config ? this.config.behavior : null,
      this.config ? !!this.config.verticalLayoutMode : null);
  }

  addNewNodeClicked() {
    this.modeler.get(InjectionNames.eventBus).on(EventType.NODE_ADDED, event => {
      this.dispatchNodeCreated(event.node, event.nodeType, event.data);
    });
  }

  addOnToggleSpinner() {
    this.modeler.get(InjectionNames.eventBus).on(EventType.TOGGLE_SPINNER, event => {
      this.toggleSpinner();
    });
  }

  addOnNodeDeleted(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.NODE_REMOVED, event => {
      const shapes = event.elements;
      shapes.forEach(shape => {
        if (shape.type === subProcessType || shape.type === taskType) {
          this.store$.dispatch(new NodeDeleted({nodeId: shape.id}));
        }
      });
    });
  }

  addOnOpenDeletePopup() {
    this.modeler.get(InjectionNames.eventBus).on(EventType.OPEN_DELETE_WARNING, event => {
      this.openDeletePopup(event.element);
    });
  }

  addOnFloatedNodesAdded(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.FLOATED_NODES_ADDED, event => {
      const floatedNodes = event.nodes;
      const layer = event.layer;
      this.store$.dispatch(new FloatedNodesAdded({layer: layer, nodes: floatedNodes}));
    });
  }

  addOnFloatedNodesRemoved(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.FLOATED_NODES_REMOVED, event => {
      const floatedNodes = event.nodes;
      const layer = event.layer;
      if (!isEmpty(floatedNodes)) {
        this.store$.dispatch(new FloatedNodesRemoved({layer: layer, nodes: floatedNodes}));
      }
    });
  }

  stopOnDoubleClick(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.ELEMENT_DBLCLICK, 1500, event => {
      // stop propagating the event to prevent the default behavior
      event.stopPropagation();
    });
  }

  private closePanelCallback() {
    this.modelerPanelService.closePanel();
  }

  private openBuilderCallback(event) {
    this.store$.dispatch(new ClearNodeConfigurationData());
    this.openBuilder(event);
  }

  private selectNodeCallback(node) {
    this.modeler.get(InjectionNames.selection).select(node);
    this.modeler.get(InjectionNames.contextPad).close();
  }

  addOnClick(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.ELEMENT_CLICK, 3500, event => {
      this.timer = setTimeout(() => {
        if (this.timer) {
          this.nodePanelConfigurationService.isNodeContentChanged(EventType.ELEMENT_CLICK, this, this.closePanelCallback, event, this.selectNodeCallback, event);
          this.nodeSelected.emit(event);
        }
      }, 300);
    });
  }

  addOnDblClickAndOpenPanel(): any {
    this.modeler.get(InjectionNames.eventBus).on([EventType.ELEMENT_DBLCLICK, EventType.OPEN_PANEL], 1500, event => {
      clearTimeout(this.timer);
      this.timer = null;
      this.nodePanelConfigurationService.isNodeContentChanged(EventType.ELEMENT_DBLCLICK, this, this.openBuilderCallback, event, this.selectNodeCallback, this.selectedNode);
      this.nodeSelected.emit(event);
      return true;
    });
  }

  private openBuilder(event) {
    this.modeler.get(InjectionNames.contextPad).close();
    const element = event.element;

    const nodeType = ModelingUtil.getNodeType(element);
    if (nodeType !== undefined) {
      this.nodeBpmnCreationResult$(element.id)
        .pipe(tap((bpmnNodeCreatedResult: BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>) => {
          this.nodeBuilderMode = bpmnNodeCreatedResult && bpmnNodeCreatedResult.data ? 'edit' : 'create';
        }), takeUntil(this.destroyed$))
        .subscribe(nodeCreationData => {
          const data = nodeCreationData ? Object.assign({id: nodeCreationData.id}, nodeCreationData.data) : {id: element.id};
          this.openNodeBuilder(data, nodeType, element);
        });
    }
  }

  selectionChangeCallback(event) {
    if (event.newSelection.length === 1) {
      const element = event.newSelection[0];
      const nodeType = ModelingUtil.getNodeType(element);
      if (nodeType !== undefined) {
        if (this.clearNodeDataOnNodeUnSelection && nodeType !== NodeType.treatment &&
          event.oldSelection.length > 0 && ModelingUtil.getNodeType(event.oldSelection[0]) === NodeType.treatment) {
          this.clearPanelNodeData(event, event.oldSelection[0]);
        } else {
          this.getModelerContext$().pipe(first()).subscribe(modelerContext => {
            this.nodeBuilderMode = (!!modelerContext) ? 'edit' : 'create';
            modelerContext['id'] = element.id;
            this.openNodeBuilder(modelerContext, nodeType, element);
          });
        }

        if (this.useSideBarComponent && nodeType === NodeType.treatment)
          this.toggleSideBar(true);
      }
    } else if (this.clearNodeDataOnNodeUnSelection && event.oldSelection.length > 0 && ModelingUtil.getNodeType(event.oldSelection[0]) === NodeType.treatment) {
      // same node or 'white space' clicked when a previous selected TP node data is exists
      this.clearPanelNodeData(event, event.oldSelection[0]);
    } else {
      // same node or 'white space' clicked
      if (!this.useSideBarComponent) {
        this.modelerPanelService.closePanel();
      }
    }
  }

  addOnSelectionChange(): any {
    this.modeler.get(InjectionNames.eventBus).on(EventType.SELECTION_CHANGED, event => {
      this.nodePanelConfigurationService.isNodeContentChanged(EventType.SELECTION_CHANGED, this, this.selectionChangeCallback, event, null, this.selectedNode);
      this.nodeSelected.emit(event);
    });
  }

  onNodeUpdated(node: Shape, nodeCreationResult: NodeCreationResult) {
    if (nodeCreationResult && !!node) {
      const nodeType = ModelingUtil.getNodeType(node);
      if (this.nodeBuilderMode === 'create') {
        this.pathwaysModeling.createNodeData(node, nodeCreationResult, nodeType);
      } else if (this.nodeBuilderMode === 'edit') {
        this.pathwaysModeling.resetNodeData(node, nodeCreationResult, nodeType);
      }
    }

    if (!this.useSideBarComponent) {
      this.modelerPanelService.closePanel();
    }

    this.store$.dispatch(new ClearNodeConfigurationData());
  }

  onDataUpdated(node: Shape, nodeCreationResult: NodeCreationResult, isUpdateNodeProperties: boolean = false, removedNode: string = null) {
    this.nodeDataUpdated.emit(nodeCreationResult);

    if (isUpdateNodeProperties) {
      if (removedNode) {
        let data = cloneDeep(nodeCreationResult);
        data["clinicalTrial"] = undefined;
        node = this.modeler.get(InjectionNames.elementRegistry).get(removedNode);
        this.bpmnModelerService.updateTreatmentNodeProperties(node, data);
      } else {
        this.bpmnModelerService.updateTreatmentNodeProperties(node, nodeCreationResult);
      }
    }
  }

  exportXMl() {
    this.modeler.saveXML({format: true}, function (err, xml) {
      if (err) {
        console.error(err);
      }

      console.log(xml);
    });
  }

  toggleSpinner(err?: any) {
    this.store$.dispatch(new ToggleModelerSpinner());
    if (err) {
      console.warn('error: ', err);
    }
  }

  onDoneImportXML(err: any) {
    this.toggleSpinner(err);
    this.modelerRendered.emit(true);
    // set focus here, calculated by canvas size
    this.modeler.get(InjectionNames.shapeFocus).setInitialFocus(this.config ? !!this.config.verticalLayoutMode : false);
  }

  /**
   * a method which create a uniq process Id by headNodeName(cancer type)
   */
  importInitialXMlDoneHandler() {
    this.toggleSpinner();
    // set focus here, calculated by canvas size
    this.modeler.get(InjectionNames.shapeFocus).setInitialFocus(this.config ? !!this.config.verticalLayoutMode : false);
  }

  ngOnDestroy(): void {
    this.bpmnModelerService.resetModeler();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private generateInitialDiagram(diagram, name, discipline) {
    let processName = discipline ? camelCase(name + discipline) : camelCase(name);
    if (/^\d/.test(processName)) {
      processName = this.prefixForProcessID + processName; // the process id need to be a valid QName- start with letters.
    }
    const importInitialDiagram = diagram.replace(/Process_1/g, processName);
    return importInitialDiagram
  }

  private initStore(bpmnIdentifier) {
    if (this.data) {
      this.store$.dispatch(new NodesInit(this.data.nodes));
      if (this.data.floatedNodes) {
        keys(this.data.floatedNodes).forEach((layer) => {
          this.store$.dispatch(new FloatedNodesAdded({
            nodes: this.data.floatedNodes[layer],
            layer: layer
          }))

        })
      }
    }
    this.store$.dispatch(new SetBpmnIdentifier(bpmnIdentifier));
    this.store$.dispatch(new SetModelerState({modelerState: this.state}));
    this.store$.dispatch(new UpdateNodeId({id: this.getCurrentCounter()}));
    this.store$.dispatch(new NodesClipboardReset({}));
  }

  private addOnRootNodeAdded() {
    const eventCallBack = (event) => {
      const element = event.element;
      if (element.id === HEAD_NODE_ID) {
        this.modeler.get(InjectionNames.modeling).updateProperties(element, {name: this.headNodeName});
        this.modeler.get(InjectionNames.eventBus).off(EventType.SHAPE_ADDED, eventCallBack);
        this.dispatchNodeCreated(element, NodeType.head, {
          id: element.id,
          name: this.headNodeName,
          ruleset: null
        });
      }
    };
    this.modeler.get(InjectionNames.eventBus).on(EventType.SHAPE_ADDED, eventCallBack);
  }

  private getBpmnIdentifier() {
    return this.data ? this.data.bpmnIdentifier : {
      name: this.headNodeName,
      processId: undefined,
      deploymentId: undefined
    };
  }

  private getCurrentCounter(): number {
    return this.data ? this.data.currentCounter : 0;
  }

  private dispatchNodeCreated(node: Shape, nodeType: NodeType, data?: NodeCreationResult | TreatmentPlanSelectorResult) {
    const bpmnNodeCreationResult = {
      id: node.id,
      parentNodeIds: ModelingUtil.getNodeParentsIds(node),
      type: nodeType,
      data: data
    };
    this.store$.dispatch(new NodeCreated(bpmnNodeCreationResult));
  }

  private dispatchNodeUpdated(node: Shape, data: NodeCreationResult | TreatmentPlanSelectorResult, type: NodeType) {
    var element = node;
    var registry = this.modeler.get(InjectionNames.elementRegistry);
    if (registry && node.id) {
      element = registry.get(node.id);
    }
    const bpmnNodeCreationResult = {
      id: node.id,
      parentNodeIds: ModelingUtil.getNodeParentsIds(element),
      type: type,
      data: data
    };
    this.store$.dispatch(new NodeUpdated(bpmnNodeCreationResult));
  }

  private dispatchRootNodeUpdated(rootNodeName: string) {
    this.store$.dispatch(new RootNodeUpdatedName(rootNodeName));
  }

  private dispatchNodesUpdated(targets: Array<Shape>) {
    if (!isEmpty(targets)) {
      targets.forEach((target) => {
        this.bpmnModelerService.getNodeCreationInfo(target).pipe(first()).subscribe((nodes: any[]) => {
          if (nodes && nodes.length > 0 && nodes[0]) {
            const node = nodes[0];
            const nodeCreationResult = node.data;
            const nodeType = node.type;
            this.dispatchNodeUpdated(node, nodeCreationResult, nodeType);
          }
        })
      });
    }
  }

  private addOnNodeUpdated() {
    this.modeler.get(InjectionNames.eventBus).on(EventType.NODE_UPDATED, event => {
      if (event.targets) {
        this.dispatchNodesUpdated(event.targets);
      } else {
        this.dispatchNodeUpdated(event.node, event.nodeCreationResult, event.nodeType);
        if (ModelingUtil.getNodeType(event.node) === NodeType.head) {
          const rootNodeName = event.nodeCreationResult.name;
          this.dispatchRootNodeUpdated(rootNodeName);
        }
      }
    });
  }

  public openNodeBuilder(data, nodeType, element) {
    this.selectedNode = element;

    // for any node we different scenarios:
    // 1. modeler is opened for editing of pathwayBpmn with all node types
    // 2. modeler is opened for manipulations on different curation item types, such as placement
    const componentToLoadConfig: ComponentToLoadConfig = get(this.config, 'panel.componentToLoad', undefined);
    if (!!componentToLoadConfig) {
      const relatedNodeType = get(componentToLoadConfig, 'relatedNodeType', undefined);

      if (!!relatedNodeType && relatedNodeType === NodeType[nodeType]) {
        const useRevisedEntitiesRevisionsData = get(componentToLoadConfig, 'useRevisedEntitiesRevisionsData', false);
        const readOnlyMode = has(componentToLoadConfig, 'readOnly') ? get(componentToLoadConfig, 'readOnly', false) : this.panelOpenReadOnly;

        this.modelerPanelService.openNodeBuilderWithComponent(
          componentToLoadConfig.componentKey,
          componentToLoadConfig,
          data,
          useRevisedEntitiesRevisionsData ? this.revisedEntitiesRevisionsData : undefined,
          readOnlyMode,
          this.onDataUpdated.bind(this, element));
      } else {
        if (this.config.panel.nodeConfig && this.config.panel.nodeConfig.nodeBuilder) {
          const config: NodeCreationPanelConfig = get(this.config.panel.nodeConfig.nodeBuilder, NodeType[nodeType],
            get(this.config.panel.nodeConfig.nodeBuilder, 'default', undefined));
          this.modelerPanelService.openNodeBuilder(config, data, this.state === PathwaysAuthoringState.readonly, this.onNodeUpdated.bind(this, element));
        }
      }
    } else {
      switch (nodeType) {
        case NodeType.treatment: {
          if (!!this.discipline && !!this.config.panel.treatmentPlanSelector.formPerDiscipline) {
            this.config.panel.treatmentPlanSelector.formPerDiscipline.forEach(
              item => {
                if (item.code === this.discipline)
                  this.config.panel.treatmentPlanSelector.formName = item.formName;
              }
            );
          }
          this.modelerPanelService.openTreatmentPlanSelector(this.config.panel.treatmentPlanSelector, data, this.revisedEntitiesRevisionsData, this.state === PathwaysAuthoringState.readonly,
            this.onNodeUpdated.bind(this, element));
          break;
        }
        default:
          const nodeBuilder: NodeCreationPanelConfig = get(this.config.panel.nodeConfig.nodeBuilder, NodeType[nodeType],
            get(this.config.panel.nodeConfig.nodeBuilder, 'default', undefined));
          const attachmentConfig: AttachmentConfig = get(this.config.panel.nodeConfig, 'attachmentConfig', undefined);
          const config: CreationPanelConfig = {nodeBuilder: nodeBuilder, attachmentConfig: attachmentConfig};
          this.modelerPanelService.openNodeBuilder(config, data, this.state === PathwaysAuthoringState.readonly, this.onNodeUpdated.bind(this, element));
          break;
      }
    }
  }

  public toggleSideBar(event: boolean) {
    this.showSidebar = event;
    this.toggleRightSidebar.emit(event);
  }

  public setSideBarExpanderTitle(event: string) {
    this.sideBarExpanderTitle = event;
  }

  private nodeBpmnCreationResult$(nodeId: string): Observable<BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>> {
    return this.store$.pipe(select(selectNodesCreationInfo),
      map(state => state[nodeId]),
      take(1));
  }

  public getModelerContext$(): Observable<object> {
    return this.store$.pipe(
      select(selectModelerContext),
      map(context => context),
      map(context => cloneDeep(context)),
      take(1));
  }

  private getNodesCreationInfo$(): Observable<Map<string, BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>>> {
    return this.store$.pipe(select(selectNodesCreationInfo),
      map(state => state),
      take(1));
  }

  //opens popup when deleting node
  private openDeletePopup(element: Shape) {
    let removeHandler = this.removeNode.bind(this);
    let markForRemoveHandler = this.markNodeForDeletion.bind(this);
    const isConnection = ModelingUtil.isConnection(element);
    const isGateway = ModelingUtil.isGateway(element);
    const payload = this.getNodeDeletePayload(element);
    if (isConnection) {
      removeHandler = this.removeConnection.bind(this);
      markForRemoveHandler = this.markConnectionForDeletion.bind(this);
      this.deleteHandler(element, removeHandler, markForRemoveHandler);
    } else {
      if (isGateway) {
        removeHandler = this.removeNode.bind(this);
        markForRemoveHandler = this.markNodeForDeletion.bind(this);
        this.deleteHandler(element, removeHandler, markForRemoveHandler);
      } else {
        this.modalLoader.popupModal(ConfirmModalComponent, payload, ModalTypes.ALERT)
          .filter(res => res.payload === modalResponse.approve).subscribe(() => {
          this.deleteHandler(element, removeHandler, markForRemoveHandler);
        });
      }
    }
  }

  //get message for deletion of node or neighborhood
  private getNodeDeletePayload(element: Shape) {
    let message = 'A node is about to be deleted, would you like to continue?';
    let title = 'Delete Node';
    if (ModelingUtil.isNeighborhoodEntryNode(element)) {
      const nonFloatingNodesIdsInNeighborhood = this.getNonFloatingNodesInNeighborhoodForDeletion(element);
      let floatingNodesForDeletionCount = this.countFloatingNodesInNeighborhoodForDeletion(element, nonFloatingNodesIdsInNeighborhood);

      const nodesForDeletionCount = floatingNodesForDeletionCount + nonFloatingNodesIdsInNeighborhood.length;
      let nodeText = nodesForDeletionCount + ' node';
      if (nodesForDeletionCount > 1) {
        nodeText = nodesForDeletionCount + ' nodes';
        title = 'Delete Nodes';
      }
      message = `A neighborhood containing ${nodeText} is about to be deleted, would you like to continue?`
    }
    return {
      title: title,
      content: message,
      approveBtn: 'Delete',
      declineBtn: 'Cancel'
    }
  }

  private countFloatingNodesInNeighborhoodForDeletion(element: Shape, nonFloatingNodesIdsInNeighborhood: String[]) {
    let floatingNodes = 0;
    this.store$.pipe(takeUntil(this.destroyed$), select(selectNumberOfFloatedNodesByLayer(ModelingUtil.getNeighborhoodHeadByEntry(element).id, this.modeler.get(InjectionNames.elementRegistry), nonFloatingNodesIdsInNeighborhood))).subscribe(res => {
      floatingNodes = res;
    });
    return floatingNodes;
  }

  private getNonFloatingNodesInNeighborhoodForDeletion(neighbrohood: Shape) {
    return uniq(
      ModelingUtil.getTreeChildren(neighbrohood, true, true)
        .filter(element => {
          return !ModelingUtil.isGateway(element) && element.id !== END_NODE_ID &&
            !ModelingUtil.hasDeletionTrackingChange(element);
        })).map((element) => element.id);
  }

  //decide whether to remove or mark for remove
  private deleteHandler(element: Shape, removeHandler: any, markForRemoveHandler: any) { //on callback need to use the same logicfrom pathway-contextpad-provider and call the appropriate handler
    let handler = removeHandler;
    const trackingChange = ModelingUtil.getTrackingChange(element);
    const isGateway = ModelingUtil.isGateway(element);
    const isFloatedConnection = ModelingUtil.isFloatedConnection(element);
    if (!isGateway && !isFloatedConnection) {
      if (this.state === PathwaysAuthoringState.inReview) {
        if (trackingChange && trackingChange.type === 'added') {
          handler = removeHandler;
        } else {
          handler = markForRemoveHandler;
        }
      }
    } else {
      handler = removeHandler;
    }
    handler(event, element);
  }

  private removeNode(event: any, element: Shape) {
    this.pathwaysModeling.removeNode(element);
  }

  private removeConnection(event: any, element: Connection) {
    this.pathwaysModeling.deleteConnection(element);
  }

  private markNodeForDeletion(event: any, element: Shape) {
    this.pathwaysModeling.markNodeDeleted(element);
  }

  private markConnectionForDeletion(event: any, element: Connection) {
    this.pathwaysModeling.markConnectionDeleted(element);
  }

  toggleOpenPanel($event: boolean) {
    this.toggleRightPanel.emit($event);
  }

  private clearPanelNodeData(event, element) {
    this.getModelerContext$().pipe(first()).subscribe(modelerContext => {
      modelerContext['id'] = null;
      this.openNodeBuilder(modelerContext, ModelingUtil.getNodeType(element), element);
    });
  }
}
